import React, { useState } from "react";
import { CheckCircle } from "lucide-react";
import OtpInput from "../../../shared/inputs/OtpInput";
import WhatsAppBtn from "../../../shared/inputs/WhatsAppBtn";
import "./styles/Signup.css";
import genericUtility from "../../../../services/utility/GenericUtility";
import UserApiService from "../../../../services/apiServices/UserApiService";
import { useNotification } from "../../../shared/contexts/NotificationContext";
import { NotificationType } from "../../../../constants/Enums";
import { closeAuth } from "../../../../dataManagement/uiDb/GeneralDb";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import { useDispatch } from "react-redux";

const ForgotPassword: React.FC = () => {
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const { triggerNotification, startLoading, stopLoading } = useNotification();
  const dispatch: AppDispatch = useDispatch();

  React.useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const [errors, setErrors] = useState({
    phone: "",
    password: "",
    confirmPassword: "",
    otp: "",
  });

  const validate = (otpAsWell: boolean = false): boolean => {
    let valid = true;
    const newErrors = { phone: "", password: "", confirmPassword: "", otp: "" };

    // Phone validation
    if (!phone.trim()) {
      newErrors.phone = "Phone number is required.";
      valid = false;
    } else if (!/^[0-9]{10}$/.test(phone)) {
      newErrors.phone = "Enter a valid 10-digit phone number.";
      valid = false;
    }

    // Password validation
    if (!password.trim()) {
      newErrors.password = "Password is required.";
      valid = false;
    } else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters.";
      valid = false;
    }

    // Confirm password validation
    if (!confirmPassword.trim()) {
      newErrors.confirmPassword = "Please re-enter password.";
      valid = false;
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match.";
      valid = false;
    }

    // otp validation
    if (otpAsWell) {
      if (!otp.trim()) {
        newErrors.otp = "OTP is required.";
        valid = false;
      } else if (otp.length != 6) {
        newErrors.otp = "OTP must be 6 characters.";
        valid = false;
      }
    }

    setErrors(newErrors);
    return valid;
  };

  const handleGetOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    // Simulate OTP sent

    const response = await UserApiService.sendOtp(phone).catch((err) => {
      setErrors((prev) => ({
        ...(prev ?? {}),
        otp: "Couldnt send otp due to unknown server error",
      }));
      console.error("Couldnt send otp due to unknown server error:", err);
      return;
    });

    if (!response) {
      setErrors((prev) => ({
        ...(prev ?? {}),
        otp: "Check your internet connection",
      }));
      return;
    } else if (response.contents && response.contents[0].includes("Active")) {
      setOtpSent(true);
      setCountdown(60);
      return;
    } else if (response.contents && response.contents[0].includes("sent")) {
      setOtpSent(true);
      setCountdown(60);
      return;
    }

    setOtpSent(true);
    setCountdown(60);
    setOtp("");
    setErrors((prev) => ({ ...prev, otp: "" }));
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate(true)) return;

    startLoading("Updating your password...");
    try {
      const payload = {
        phonenumber: phone ?? "",
        password: password ?? "",
        otp: otp ?? "",
      };

      const response = await UserApiService.forgotPassword(payload).catch(
        (err) => {
          console.error("Couldnt change password to unknown error:", err);
          return null;
        }
      );

      if (!response) {
        setErrors((prev) => ({
          ...(prev ?? {}),
          otp: "Couldnt change password due to unknown server error",
        }));
        return;
      }

      const hasError = response?.errorInformation?.length > 0;
      const isOtpError = hasError && response?.errorInformation[0].errorCode === "103";
      const isSuccess = response?.generalProcessingStatus?.processingStatus?.includes("OK") || 
                        response?.generalProcessingStatus?.processingStatus === "SUCCESS" ||
                        (!hasError && (response?.message?.includes("OK") || response?.status === "OK" || response?.contents));

      if (isOtpError) {
        setErrors((prev) => ({
          ...(prev ?? {}),
          otp: "Invalid otp, please try again",
        }));
      } else if (isSuccess) {
        dispatch(closeAuth());
        triggerNotification(
          "Successfully changed the password",
          NotificationType.SUCCESS
        );
      } else {
        setErrors((prev) => ({
          ...(prev ?? {}),
          otp: response?.errorInformation?.[0]?.errorMessage || "Technical issue, please report to support team",
        }));
      }
    } catch (error) {
      console.error(`Couldnt change password due to unknown error ${error}`);
      triggerNotification("Failed to update password. Please try again.", NotificationType.ERROR);
    } finally {
      stopLoading();
    }
  };

  return (
    <div className="signup-container">
      {/* PHONE FIELD */}
      <div className="field-group">
        <label>Phone Number</label>
        <input
          type="text"
          autoFocus
          className={errors.phone ? "input-error" : ""}
          value={phone}
          placeholder="Enter phone number"
          onChange={(e) => {
            setPhone(e.target.value);
            if (errors.phone) setErrors({ ...errors, phone: "" });
          }}
        />
        {errors.phone && <p className="error-text">{errors.phone}</p>}
      </div>

      {/* PASSWORD FIELD */}
      <div className="field-group">
        <label>Password</label>
        <input
          type="password"
          className={errors.password ? "input-error" : ""}
          value={password}
          placeholder="Create password"
          onChange={(e) => {
            setPassword(e.target.value);
            if (errors.password) setErrors({ ...errors, password: "" });
          }}
        />
        {errors.password && <p className="error-text">{errors.password}</p>}
      </div>

      {/* CONFIRM PASSWORD FIELD */}
      <div className="field-group">
        <label>Confirm Password</label>
        <input
          type="password"
          className={errors.confirmPassword ? "input-error" : ""}
          value={confirmPassword}
          placeholder="Re-enter password"
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (errors.confirmPassword)
              setErrors({ ...errors, confirmPassword: "" });
          }}
        />
        {errors.confirmPassword && (
          <p className="error-text">{errors.confirmPassword}</p>
        )}
      </div>

      {/* OTP FIELD (shown only after Get OTP) */}
      {otpSent && (
        <div className="field-group" style={{ animation: "fadeIn 0.3s ease-in-out" }}>
          <label>Enter OTP</label>
          <OtpInput 
            value={otp} 
            onChange={(val: string) => {
              setOtp(val);
              if (errors.otp) setErrors({ ...errors, otp: "" });
            }} 
            error={!!errors.otp} 
            length={6} 
          />
          {errors.otp && <p className="error-text">{errors.otp}</p>}
        </div>
      )}

      {/* WhatsApp OTP Button */}
      <WhatsAppBtn 
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          if (!otpSent || countdown === 0) handleGetOtp(e as any);
        }}
        disabled={otpSent && countdown > 0}
        otpSent={otpSent}
        countdown={countdown}
      />

      <button
        className="primary-btn"
        onClick={handleChangePassword}
        disabled={!otpSent}
      >
        Change Password
      </button>
    </div>
  );
};

export default ForgotPassword;
